import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Warning,
  Schedule,
  TrendingUp,
  TrendingDown,
  Visibility
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';

function ProjectReports() {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 3)),
    endDate: new Date(),
    status: 'all',
    departmentId: 'all'
  });
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchDepartments();
    fetchReport();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/reports/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: filters.startDate.toISOString().split('T')[0],
        endDate: filters.endDate.toISOString().split('T')[0],
        status: filters.status,
        departmentId: filters.departmentId
      };
      
      const response = await api.get('/reports/projects', { params });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching project report:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(value || 0);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      active: 'primary',
      completed: 'success',
      pending: 'warning',
      cancelled: 'error',
      'on-hold': 'default'
    };
    return statusColors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const statusIcons = {
      active: <Schedule />,
      completed: <CheckCircle />,
      pending: <Warning />,
      cancelled: <Warning />,
      'on-hold': <Schedule />
    };
    return statusIcons[status] || <Assignment />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(value) => setFilters({ ...filters, startDate: value })}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(value) => setFilters({ ...filters, endDate: value })}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="on-hold">On Hold</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Department"
              value={filters.departmentId}
              onChange={(e) => setFilters({ ...filters, departmentId: e.target.value })}
            >
              <MenuItem value="all">All Departments</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={fetchReport}
              startIcon={<Assignment />}
            >
              Generate
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {reportData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Projects
                      </Typography>
                      <Typography variant="h4">
                        {reportData.summary.total}
                      </Typography>
                    </Box>
                    <Assignment color="primary" fontSize="large" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Revenue
                      </Typography>
                      <Typography variant="h5">
                        {formatCurrency(reportData.summary.totalRevenue)}
                      </Typography>
                    </Box>
                    <TrendingUp color="success" fontSize="large" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Expenses
                      </Typography>
                      <Typography variant="h5">
                        {formatCurrency(reportData.summary.totalExpenses)}
                      </Typography>
                    </Box>
                    <TrendingDown color="error" fontSize="large" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Profit
                      </Typography>
                      <Typography variant="h5" color={reportData.summary.totalProfit >= 0 ? 'success.main' : 'error.main'}>
                        {formatCurrency(reportData.summary.totalProfit)}
                      </Typography>
                    </Box>
                    {reportData.summary.totalProfit >= 0 ? 
                      <TrendingUp color="success" fontSize="large" /> :
                      <TrendingDown color="error" fontSize="large" />
                    }
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Status Breakdown */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Projects by Status
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(reportData.summary.byStatus || {}).map(([status, count]) => (
                    <Grid item xs={6} key={status}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(status)}
                        <Typography variant="body1">
                          {status.charAt(0).toUpperCase() + status.slice(1)}: <strong>{count}</strong>
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Projects by Department
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(reportData.summary.byDepartment || {}).map(([dept, count]) => (
                    <Grid item xs={12} key={dept}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1">{dept}</Typography>
                        <Chip label={count} color="primary" size="small" />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          {/* Projects Table */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Project Details
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Expenses</TableCell>
                    <TableCell align="right">Profit</TableCell>
                    <TableCell align="center">Margin</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.projects.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {project.title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(project.start_date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>{project.client_name || 'N/A'}</TableCell>
                      <TableCell>{project.department_name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={project.status} 
                          color={getStatusColor(project.status)}
                          size="small"
                          icon={getStatusIcon(project.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(project.project_revenue)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(project.project_expenses)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          color={project.profit >= 0 ? 'success.main' : 'error.main'}
                          fontWeight="medium"
                        >
                          {formatCurrency(project.profit)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={`${project.profitMargin}%`}
                          color={project.profitMargin >= 20 ? 'success' : project.profitMargin >= 0 ? 'warning' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}

export default ProjectReports;
